import axios from 'axios';

// Create axios instance with base URL and default headers
// GO_SERVER_API_URL has no VITE_ prefix so Vite never exposes it to client
// code — this always falls through to the default. Use '/api' (relative,
// works via nginx same-origin proxying) to match config/axios.ts instead of
// 'http://localhost:8080/api', which broke this in every production build.
const api = axios.create({
  baseURL: import.meta.env.GO_SERVER_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Cookie-based token retrieval function
function getTokenFromCookie(): string | null {
  const cookies = document.cookie.split(';');
  const tokenCookie = cookies.find(cookie => cookie.trim().startsWith('auth_token='));
  if (tokenCookie) {
    const tokenValue = tokenCookie.split('=')[1];
    if (tokenValue) {
      return tokenValue.trim();
    }
  }
  return null;
}

// Request interceptor for adding auth token if available
api.interceptors.request.use(
  (config) => {
    // Try to get token from cookie
    const token = getTokenFromCookie();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling common errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      // Log detailed error information
      const { status, data, config } = error.response;
      console.error('API Error Response:', {
        status,
        url: config?.url,
        method: config?.method,
        data: data,
        headers: config?.headers,
      });
      
      // Handle specific status codes
      switch (status) {
        case 400:
          console.error('Bad Request - The request was invalid:', data);
          break;
        case 401:
          console.error('Unauthorized - Authentication is required');
          break;
        case 403:
          console.error('Forbidden - You do not have permission to access this resource');
          break;
        case 404:
          console.error('Not Found - The requested resource was not found');
          break;
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received:', error.request);
    } else {
      // Something happened in setting up the request
      console.error('Request setup error:', error.message);
    }
    return Promise.reject(error);
  }
);

export default api;
